import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {invalidateOnSuccess, metadaApi} from '@dms/api/base';

import type {CheckoutNotificationApiCall} from './types';

export type CallNotificationActionArg = CheckoutNotificationApiCall & {
  checkoutId: string;
  paymentId?: string;
  invalidateScope?: 'paymentsList' | 'checkout';
};

/**
 * Non-generated wrapper for dynamic notification action API calls.
 * The backend returns notification buttons with dynamic path/method/payload;
 * this cannot be expressed as a fixed OpenAPI endpoint.
 * Kept separate from api.ts so it is not lost during OpenAPI regeneration.
 * Pass invalidateScope so the mutation can invalidate tags on success (no dispatch in callers).
 */
export const metadaAftersalesCheckoutNotificationApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    callNotificationAction: build.mutation<void, CallNotificationActionArg>({
      query: (arg) => ({
        url: arg.path,
        method: arg.method,
        body: arg.payload ?? undefined,
      }),
      invalidatesTags: invalidateOnSuccess((_result, arg: CallNotificationActionArg) => {
        const paymentTag = isNotNil(arg.paymentId)
          ? [{type: 'afsPaymentById', id: arg.paymentId} as const]
          : [];

        return match(arg.invalidateScope)
          .with('paymentsList', () => [
            {type: 'afsPaymentsList', id: arg.checkoutId} as const,
            ...paymentTag,
          ])
          .with('checkout', () => [
            {type: 'afsCheckout', id: arg.checkoutId} as const,
            {type: 'afsPaymentsList', id: arg.checkoutId} as const,
            ...paymentTag,
          ])
          .with(undefined, () => [])
          .exhaustive();
      }),
    }),
  }),
});

export const {useCallNotificationActionMutation} = metadaAftersalesCheckoutNotificationApi;
